import { useState, useCallback, useMemo, useEffect } from 'react';
import { getSalesCatalogForVendible } from '../services/salesService';
import { productService } from '../services/productService';

const isOfferCatalogId = (id) => String(id ?? '').startsWith('offer-');

/**
 * Catálogo de productos vendibles y ofertas activas en un punto de venta (Ventas).
 */
export function useVentasProductCatalog(pointOfSaleId) {
  const [byId, setById] = useState({});
  const [offersById, setOffersById] = useState({});

  const mergeProducts = useCallback((list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    setById((prev) => {
      const next = { ...prev };
      list.forEach((product) => {
        if (product?.id != null && !isOfferCatalogId(product.id)) {
          next[product.id] = { ...prev[product.id], ...product, catalog_type: 'product' };
        }
      });
      return next;
    });
  }, []);

  const mergeOffers = useCallback((list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    setOffersById((prev) => {
      let changed = false;
      const next = { ...prev };
      list.forEach((offer) => {
        if (offer?.id == null) return;
        const previous = prev[offer.id];
        const merged = { ...previous, ...offer, catalog_type: 'offer' };
        if (
          !previous ||
          previous.name !== merged.name ||
          previous.offer_kind !== merged.offer_kind ||
          previous.catalog_selection_id !== merged.catalog_selection_id
        ) {
          next[offer.id] = merged;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    const productsFromOffers = (list || []).flatMap((offer) =>
      (offer.items || []).map((item) => ({
        id: item.product_id,
        sku: item.sku,
        name: item.name,
        unit_of_measure: item.unit_of_measure,
        list_price: item.base_price,
        effective_price: item.offer_price,
      })),
    );
    mergeProducts(productsFromOffers);
  }, [mergeProducts]);

  const mergeCatalogResponse = useCallback(
    (res) => {
      mergeProducts(res?.data || []);
      mergeOffers(res?.offers || []);
    },
    [mergeOffers, mergeProducts],
  );

  const getCatalogEntry = useCallback(
    (id, fallback = null) => {
      if (id == null || id === '') return fallback;
      const key = String(id);
      if (offersById[key]) {
        return offersById[key];
      }
      if (isOfferCatalogId(key)) {
        return offersById[key] || fallback;
      }
      const numericId = parseInt(key, 10);
      if (!numericId) return fallback;
      return byId[numericId] || fallback;
    },
    [byId, offersById],
  );

  const getProduct = useCallback(
    (id, fallback = null) => {
      const entry = getCatalogEntry(id, fallback);
      if (entry?.catalog_type === 'offer') return fallback;
      return entry;
    },
    [getCatalogEntry],
  );

  const searchProducts = useCallback(
    async (query) => {
      if (!pointOfSaleId) return [];
      const res = await getSalesCatalogForVendible(query, pointOfSaleId, true);
      mergeCatalogResponse(res);
      const products = (res?.data || []).map((p) => ({ ...p, catalog_type: 'product' }));
      const offers = (res?.offers || []).map((o) => ({ ...o, catalog_type: 'offer' }));
      return [...offers, ...products];
    },
    [mergeCatalogResponse, pointOfSaleId],
  );

  const ensureByIds = useCallback(
    async (ids) => {
      const unique = [...new Set((ids || []).map((id) => parseInt(id, 10)).filter(Boolean))];
      const missing = unique.filter((id) => !byId[id]);
      if (missing.length === 0) return [];

      const response = await productService.batchForForm(missing);
      const list = response?.data || [];
      mergeProducts(list);
      return list;
    },
    [byId, mergeProducts],
  );

  const resolveIdentifiers = useCallback(
    async (identifiers) => {
      if (!pointOfSaleId) {
        const response = await productService.resolveIdentifiersForForm(identifiers);
        const list = response?.data || [];
        mergeProducts(list);
        return list;
      }

      const res = await getSalesCatalogForVendible('', pointOfSaleId, true);
      mergeCatalogResponse(res);

      const vendible = (res?.data || []).map((p) => ({ ...p, catalog_type: 'product' }));
      const bySku = new Map(vendible.map((p) => [String(p.sku || '').toLowerCase(), p]));
      const byIdMap = new Map(vendible.map((p) => [String(p.id), p]));

      return (identifiers || [])
        .map((raw) => {
          const key = String(raw ?? '').trim();
          if (!key) return null;
          return byIdMap.get(key) || bySku.get(key.toLowerCase()) || null;
        })
        .filter(Boolean);
    },
    [mergeCatalogResponse, pointOfSaleId],
  );

  useEffect(() => {
    if (!pointOfSaleId) {
      setById({});
      setOffersById({});
      return;
    }
    getSalesCatalogForVendible('', pointOfSaleId, true)
      .then((res) => mergeCatalogResponse(res))
      .catch(() => {});
  }, [pointOfSaleId, mergeCatalogResponse]);

  const products = useMemo(() => Object.values(byId), [byId]);
  const offers = useMemo(() => Object.values(offersById), [offersById]);
  const catalogEntries = useMemo(() => [...offers, ...products], [offers, products]);

  return {
    products,
    offers,
    catalogEntries,
    byId,
    offersById,
    getProduct,
    getCatalogEntry,
    mergeProducts,
    mergeOffers,
    ensureByIds,
    searchProducts,
    resolveIdentifiers,
  };
};
